import { Button, FormWarning, Modal } from "@shltelemedicine/shl-ui-lib";
import { useCallback, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { emailPattern } from "../../../../constants";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import FindingsIcon from "../../../Shared/FindingsIcon/FindingsIcon";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";

type SendMailModalProps = {
  show?: boolean;
  onSend?: (emails: string[]) => Promise<void>;
  onClose?: () => void;
};

type SendMailModalFormInputs = {
  email: string;
};

const SendMailModal = ({ show, onSend, onClose }: SendMailModalProps) => {
  const intl = useIntl();
  const {
    control,
    handleSubmit,
    watch,
    formState: {
      isSubmitting,
      errors: { email: emailError },
    },
  } = useForm<SendMailModalFormInputs>();

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (show) {
      inputRef.current?.focus();
    }
  }, [show]);

  const sendViaMailPlaceholder = intl.messages["GLOBAL.INSERT_HERE"] as string;

  const sendMailHandler = useCallback(
    async (emails: string[]) => {
      onSend && (await onSend(emails));
    },
    [onSend]
  );

  const footerProps: FooterProps = {
    loading: isSubmitting,
    disabled: isSubmitting || !watch("email"),
    onSend: handleSubmit(({ email }) => sendMailHandler([email])),
    onClose,
  };

  return (
    <>
      <Modal
        centered
        backdrop="static"
        show={show}
        onHide={onClose}
        header={<Header />}
        footer={<Footer {...footerProps} />}
      >
        <Form className="px-5 py-2">
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 font-md d-flex align-items-center">
              <span className="prim-color">*</span>
              &nbsp;
              <FormattedMessage id="USER.EMAIL" defaultMessage="Email" />:
              <span className="mx-1"></span>
              {!!emailError && (
                <FormWarning
                  className="font-sm flex-grow-1"
                  message={
                    <FormattedMessage
                      id={`${
                        emailError.type === "pattern"
                          ? "ECG_DETAILS.SEND_VIA_EMAIL.EMAIL_PATTERN_REQUIRED"
                          : "ECG_DETAILS.SEND_VIA_EMAIL.EMAIL_REQUIRED"
                      }`}
                      defaultMessage={`${
                        emailError.type === "pattern"
                          ? "Insert Valid Email"
                          : "Insert Email"
                      }`}
                    />
                  }
                />
              )}
            </Form.Label>
            <TextFormInput
              name={"email"}
              control={control}
              rules={{
                required: true,
                pattern: emailPattern,
              }}
              input={{
                placeholder: sendViaMailPlaceholder,
                isInvalid: !!emailError,
                disabled: isSubmitting,
              }}
            />
          </Form.Group>
        </Form>
      </Modal>
    </>
  );
};

export default SendMailModal;

const Header = () => (
  <div className="d-flex align-items-center">
    <FindingsIcon />
    <span className="mx-1"></span>
    <p className="mb-0">
      <FormattedMessage
        id="ECG_DETAILS.SEND_VIA_EMAIL"
        defaultMessage="Send ECG via Email"
      />
    </p>
  </div>
);

type FooterProps = {
  loading?: boolean;
  disabled?: boolean;
  onClose?: () => void;
  onSend?: () => void;
};

const Footer = ({ loading, disabled, onSend, onClose }: FooterProps) => {
  return (
    <div className="d-flex align-items-center justify-content-end border-top pt-2 px-3 m-sm-0">
      <CancelButton onClick={onClose} disabled={loading} />
      <span className="mx-2"></span>
      <Button
        bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
        disabled={disabled}
        onClick={onSend}
      >
        <span className="capitalized">
          <FormattedMessage id="GLOBAL.SEND" defaultMessage="Send" />
        </span>
        {loading && (
          <>
            &nbsp;&nbsp;
            <Spinner as="span" animation="border" size="sm" />
          </>
        )}
      </Button>
    </div>
  );
};
